import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EventService } from '../../services/APIService';
import { baseURL } from '../../services/APIService';
const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    capacity: '',
    registrationRequired: false,
    image: null
  });
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch all events
  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await EventService.getAllEvents();
      
      // Handle different response structures
      if (Array.isArray(response)) {
        setEvents(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        // If the API returns an object with a data array (new format)
        setEvents(response.data);
      } else if (response && response.events && Array.isArray(response.events)) {
        // If the API returns an object with an events array
        setEvents(response.events);
      } else if (response && typeof response === 'object') {
        // If the API returns an object structure with event objects
        const eventsArray = Object.values(response).filter(item => 
          item && typeof item === 'object' && 
          (item.title || item.date || item.description)
        );
        
        if (eventsArray.length > 0) {
          setEvents(eventsArray);
        } else {
          console.error('Unexpected API response format:', response);
          setError('Received unexpected data format from the server.');
          setEvents([]); // Set to empty array to avoid .map errors
        }
      } else {
        console.error('Unexpected API response format:', response);
        setError('Received unexpected data format from the server.');
        setEvents([]); // Set to empty array to avoid .map errors
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events. Please try again.');
      setEvents([]); // Set to empty array to avoid .map errors
    } finally {
      setLoading(false);
    }
  };

  // Open modal to create a new event
  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setFormData({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      capacity: '',
      registrationRequired: false,
      image: null
    });
    setPreviewImage(null);
    setModalOpen(true);
  };

  // Open modal to edit an event
  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
      time: event.time || '',
      location: event.location || '',
      capacity: event.capacity || '',
      registrationRequired: event.registrationRequired || false
    });
    
    // Handle different image sources
    if (event.imageData) {
      setPreviewImage(event.imageData);
    } else if (event.imageUrl) {
      setPreviewImage(event.imageUrl);
    } else if (event.hasImage) {
      // If we know there's an image but don't have direct data, we can use the API URL
      // Use the direct API URL for better performance
      setPreviewImage(`${baseURL}/events/image/${event.id}`);
    } else {
      setPreviewImage(null);
    }
    
    setModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      setFormData({
        ...formData,
        [name]: file
      });
      
      // Create a preview URL for the image
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImage(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (selectedEvent) {
        // Update existing event - handle both id and _id formats
        const eventId = selectedEvent.id || selectedEvent._id;
        await EventService.updateEvent(eventId, formData);
      } else {
        // Create new event
        await EventService.createEvent(formData);
      }
      
      // Close modal and refresh events list
      setModalOpen(false);
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      setError('Failed to save event. Please try again.');
    }
  };

  // Delete event
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }
    
    try {
      await EventService.deleteEvent(eventId);
      // Update events list - handle both id and _id formats
      setEvents(events.filter(event => {
        // Check both id and _id formats
        const eventIdentifier = event.id || event._id;
        return eventIdentifier !== eventId;
      }));
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Failed to delete event. Please try again.');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create, update and manage events for Karigar Foundation
          </p>
        </div>
        <button
          onClick={handleCreateEvent}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create New Event
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.length > 0 ? (
            events.map(event => (
              <div key={event.id || event._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {(event.hasImage || event.imageUrl || event.imageData) && (
                  <div className="relative h-48">
                    <img 
                      src={event.imageData ? event.imageData : (event.imageUrl ? event.imageUrl : (event.hasImage ? `${baseURL}/events/image/${event.id || event._id}` : null))} 
                      alt={event.title} 
                      className="w-full h-full object-cover" 
                    />
                    
                    {/* Date & Time badge - styled like a peeling corner sticker */}
                    <div className="absolute top-0 left-0 w-20 h-20">
                      <div className="absolute top-0 left-0 w-full h-full bg-white transform origin-top-left rotate-0 shadow-lg rounded-br-lg">
                        <div className="w-full h-full flex flex-col items-center justify-center text-center pt-1">
                          <span className="text-xl font-bold text-indigo-700">
                            {new Date(event.date).getDate()}
                          </span>
                          <span className="text-xs uppercase font-bold tracking-wide text-indigo-600">
                            {new Date(event.date).toLocaleString('default', { month: 'short' })}
                          </span>
                          <span className="text-xs text-gray-600 mt-1">
                            {event.time || (new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}))}
                          </span>
                        </div>
                      </div>
                      {/* Peeling effect - small triangle */}
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-gray-300 transform rotate-45 translate-y-1 translate-x-1"></div>
                    </div>
                  </div>
                )}
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{event.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {formatDate(event.date)} {event.time && `at ${event.time}`}
                  </p>
                  {event.location && (
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Location:</span> {event.location}
                    </p>
                  )}
                  {event.capacity && (
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Capacity:</span> {event.capacity}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mb-4 line-clamp-3">{event.description}</p>
                  
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        new Date(event.date) >= new Date() 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {new Date(event.date) >= new Date() ? 'Upcoming' : 'Past'}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <Link 
                        to={`/admin/events/${event.id || event._id}/registrations`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Registrations
                      </Link>
                      <button 
                        onClick={() => handleEditEvent(event)}
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteEvent(event.id || event._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-10 bg-white rounded-lg shadow">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No events found</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new event.</p>
              <div className="mt-6">
                <button
                  onClick={handleCreateEvent}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Create New Event
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event Modal (Create/Edit) */}
      {modalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {selectedEvent ? 'Edit Event' : 'Create New Event'}
                  </h3>
                  
                  <div className="mb-4">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                      Event Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                        Date *
                      </label>
                      <input
                        type="date"
                        name="date"
                        id="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        name="time"
                        id="time"
                        value={formData.time}
                        onChange={handleInputChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      id="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
                      Capacity
                    </label>
                    <input
                      type="number"
                      name="capacity"
                      id="capacity"
                      min="1"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows="4"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      required
                    ></textarea>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="registrationRequired"
                        id="registrationRequired"
                        checked={formData.registrationRequired}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="registrationRequired" className="ml-2 block text-sm text-gray-700">
                        Registration Required
                      </label>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event Image
                    </label>
                    <div className="mt-1 flex items-center">
                      <input
                        type="file"
                        name="image"
                        onChange={handleInputChange}
                        accept="image/*"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    {previewImage && (
                      <div className="mt-3">
                        <img 
                          src={previewImage} 
                          alt="Preview" 
                          className="h-32 w-auto object-cover rounded-md" 
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {selectedEvent ? 'Update Event' : 'Create Event'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
